import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Camera, X } from "lucide-react";
import { Button } from "@clevis/shared-ui";

// QR labels for items, and scanning with the phone's camera. A label carries
// the item's part number (or "item:<id>" when it has none), so a handheld 2D
// scanner, the camera, or typing the part number all find the same item.

export const itemCode = (item: { id: string; sku?: string | null }) => (item.sku ?? "").trim() || `item:${item.id}`;

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

/** Open a print-ready sheet of labels (3 across, about 2.6" × 1"). */
export async function printQrLabels(labels: Array<{ code: string; title: string; subtitle?: string }>, companyName = ""): Promise<void> {
  const imgs = await Promise.all(labels.map((l) => QRCode.toDataURL(l.code, { margin: 1, width: 180, errorCorrectionLevel: "M" })));
  const w = window.open("", "_blank");
  if (!w) throw new Error("Allow pop-ups for Clevis to print labels.");
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Labels</title><style>
    @page { margin: 0.5in 0.19in; }
    body { margin: 0; font-family: Helvetica, Arial, sans-serif; color: #213C4A; }
    .sheet { display: grid; grid-template-columns: repeat(3, 2.625in); grid-auto-rows: 1in; column-gap: 0.125in; }
    .label { display: flex; align-items: center; gap: 0.08in; padding: 0.06in 0.1in; overflow: hidden; break-inside: avoid; }
    .label img { width: 0.82in; height: 0.82in; flex: none; }
    .t { font-size: 10pt; font-weight: bold; line-height: 1.15; max-height: 2.3em; overflow: hidden; }
    .s { font-size: 8pt; color: #688A9D; margin-top: 2px; }
    .c { font-size: 6.5pt; color: #688A9D; margin-top: 2px; }
  </style></head><body><div class="sheet">${labels
    .map(
      (l, i) =>
        `<div class="label"><img src="${imgs[i]}" alt=""><div><div class="t">${esc(l.title)}</div>${l.subtitle ? `<div class="s">${esc(l.subtitle)}</div>` : ""}${companyName ? `<div class="c">${esc(companyName)}</div>` : ""}</div></div>`,
    )
    .join("")}</div><script>window.onload = () => { window.focus(); window.print(); };</script></body></html>`);
  w.document.close();
}

interface Detector {
  detect: (src: CanvasImageSource) => Promise<Array<{ rawValue: string }>>;
}
declare global {
  interface Window {
    BarcodeDetector?: new (opts?: { formats?: string[] }) => Detector;
  }
}

/** Phones and Chrome that can read codes from the camera (iPhone Safari can't yet: use a handheld scanner or type it). */
export const cameraScanSupported = () => typeof window !== "undefined" && !!window.BarcodeDetector && !!navigator.mediaDevices?.getUserMedia;

export function CameraScan({ onCode, onClose }: { onCode: (code: string) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState("");
  // The camera keeps running while the page behind it re-renders.
  const handler = useRef(onCode);
  handler.current = onCode;
  useEffect(() => {
    let stream: MediaStream | null = null;
    let stop = false;
    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (!video.current) return;
        video.current.srcObject = stream;
        await video.current.play();
        const detector = new window.BarcodeDetector!({ formats: ["qr_code", "code_128", "code_39", "ean_13", "ean_8", "upc_a", "upc_e"] });
        while (!stop) {
          const found = await detector.detect(video.current).catch(() => []);
          if (found[0]?.rawValue) {
            handler.current(found[0].rawValue.trim());
            return;
          }
          await new Promise((r) => setTimeout(r, 250));
        }
      } catch (err) {
        setError((err as Error).name === "NotAllowedError" ? "Allow the camera for Clevis to scan." : "The camera couldn't start.");
      }
    })();
    return () => {
      stop = true;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-ink/90 p-4">
      <video ref={video} className="max-h-[70vh] w-full max-w-md rounded-lg bg-black" playsInline muted />
      <p className="mt-3 text-sm text-white">{error || "Point the camera at the label."}</p>
      <Button variant="secondary" className="mt-3" onClick={onClose}>
        <X size={14} className="mr-1 inline" /> Close
      </Button>
    </div>
  );
}

export function CameraScanButton({ onCode }: { onCode: (code: string) => void }) {
  const [open, setOpen] = useState(false);
  if (!cameraScanSupported()) return null;
  return (
    <>
      <button type="button" className="rounded-lg border border-steel/30 p-1.5 text-steel hover:text-ink" aria-label="Scan with the camera" title="Scan with the camera" onClick={() => setOpen(true)}>
        <Camera size={16} />
      </button>
      {open ? (
        <CameraScan
          onCode={(c) => {
            setOpen(false);
            onCode(c);
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}
